/*!
 * @license
 * SPDX-License-Identifier: LicenseRef-cloudEQ-Proprietary
 * Copyright (c) 2026-present cloudEQ Inc. All Rights Reserved.
 *
 * PROPRIETARY AND CONFIDENTIAL.
 *
 * This source code, including all associated files, documentation, and
 * compiled forms, is the exclusive property of cloudEQ Inc. and is
 * protected by copyright, trade secret, and other intellectual property laws.
 *
 * This software is licensed, not sold, solely for use by authorized
 * licensees under the terms of a written license agreement with
 * cloudEQ Inc. Unless expressly permitted by that agreement, you may not
 * copy, modify, merge, distribute, sublicense, sell, publish, reverse
 * engineer, decompile, disassemble, or create derivative works of this
 * software, in whole or in part, by any means.
 *
 * Access to this file does not grant any license or right to use it.
 * Unauthorized use, reproduction, or disclosure is strictly prohibited and
 * may result in civil and criminal penalties.
 *
 * Contact: legal@cloudeq.com
 */

import { useState } from 'react';
import { ChevronDown } from 'lucide-react';
import * as Popover from '@radix-ui/react-popover';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { activeCircleLabel, sessionKeys, useCircles } from '@/queries/session';
import { taskKeys } from '@/queries/tasks';
import { Tip } from '@/app/Tip';
import { CopyCircleLink } from '@/board/CopyCircleLink';
import { cn } from '@/lib/cn';

/**
 * Which board you are looking at.
 *
 * **Not rendered at all for somebody with a single circle** — `canSwitch` is
 * false and there is nothing to switch between; a disabled control would only
 * invite a click.
 *
 * The choice is **server state**: POST /api/circles/active remembers it per
 * person, so it survives a sign-out and follows you to another machine. It is
 * deliberately not in the URL — a link carrying it would silently switch the
 * circle of whoever opened it.
 */
// A new key, deliberately not folded into an existing one: renaming or reusing
// a storage key silently discards what people already had saved.
const ALL_CIRCLES_KEY = 'qollabShowAllCircles';

export function CircleSwitcher() {
  const { data } = useCircles();
  const qc = useQueryClient();
  // Controlled so choosing can close it. Radix keeps a popover open when a child
  // is clicked, which left the menu covering the board it had just switched —
  // and made a second switch impossible without a click that read as "close".
  const [open, setOpen] = useState(false);

  // Whether the circles you are not in are folded away. Remembered on the
  // device rather than on the server: it is a preference about this menu, not
  // about the account, and the active circle — which *is* account state — is
  // already persisted separately.
  const [showAll, setShowAll] = useState(() => {
    try { return localStorage.getItem(ALL_CIRCLES_KEY) === '1'; } catch { return false; }
  });
  const toggleAll = () => setShowAll((v) => {
    // A private window can throw on write. Losing the preference is not worth
    // failing the click over.
    try { localStorage.setItem(ALL_CIRCLES_KEY, v ? '0' : '1'); } catch { /* not important enough to fail */ }
    return !v;
  });

  const choose = useMutation({
    mutationFn: (circleId: string) =>
      api.post<{ activeId: string; mode: string }>('/api/circles/active', { circleId }),
    onSuccess: async () => {
      setOpen(false);
      // Both, and in this order: the circles response carries the new activeId,
      // and every task list is keyed by circle so the old one must not be shown
      // for a beat while the new one loads.
      await qc.invalidateQueries({ queryKey: sessionKeys.circles });
      await qc.invalidateQueries({ queryKey: taskKeys.all });
    },
  });

  if (!data?.canSwitch) return null;

  const active = data.circles.find((c) => c.id === data.activeId);
  const label = activeCircleLabel(data);

  // **The circles you are in, and the ones you can merely reach.**
  //
  // Identical lists for everybody except a super admin, who can open every
  // circle in the company. Sean's menu had eight entries and he works in three;
  // the five he does not were not information, they were the reason the three
  // were hard to find.
  //
  // `isMember !== false` rather than `=== true`: an older server does not send
  // the field, and reading absent as "not a member" would empty the menu.
  const mine = data.circles.filter((c) => c.isMember !== false);
  const others = data.circles.filter((c) => c.isMember === false);

  // The circle on screen is always listed, member or not. It is the one entry
  // that must be there — a menu that omits what it is currently showing reads
  // as a bug, and the tick beside it is how you know where you are.
  const shown = showAll
    ? [...mine, ...others]
    : mine.concat(others.filter((c) => c.id === data.activeId));

  return (
    <Popover.Root open={open} onOpenChange={setOpen}>
      {/* **One control, not three.** This used to be a coloured ring with the
          circle's initial in it and a 16px chevron badge hanging off its corner,
          sitting beside a separate `<h1>` that printed the name. Three pieces
          saying one thing, and only the smallest of them looked pressable — the
          name, which is the part people actually read, was not part of the
          control at all.

          It is now a single pill: swatch, name, chevron. The heading did not
          disappear; `#board-title` is the name span inside this button, so the
          circle is still named exactly once, and `board-header` still reads it
          the same way. */}
      <Tip label={`Switch circle — currently ${label}`}>
        <Popover.Trigger asChild>
          <button
            id="circle-btn"
            // Both the accessible name and the tooltip come from
            // `activeCircleLabel`, the same helper the name span uses. Two
            // spellings of one state is how they drift apart.
            aria-label={`Switch circle — currently ${label}`}
            // `circle-wrap` is what topbar-responsive measures for overlap. It
            // sits on the button rather than a wrapper on purpose: that probe
            // compares leaf rectangles, and a wrapper's box is not what people
            // see collide.
            className="tap circle-wrap group -ml-2 flex min-w-0 shrink items-center gap-2.5 rounded-xl border border-transparent px-2 py-1 text-left transition-colors duration-fast ease-out-soft hover:border-border hover:bg-bg data-[state=open]:border-border data-[state=open]:bg-bg"
          >
            {/* **A hollow ring, and the colour is a stroke rather than a fill.**
                The middle is genuinely empty — no white disc behind it — so it
                sits on the bar's gradient, on the hover fill and on dark without
                any of those being named here. `index.css` draws it; this only
                supplies the colour, twice: `borderColor` is the ring itself, and
                `--dot` is what the glow is mixed from.

                **`board-header` reads this element's colour** to check the
                switcher is painted from the same helper as the circle's own
                swatch in the menu below. That test now falls back from
                `background-color` to `border-color`, which is where the colour
                went. There is always exactly one circle on screen, so there is
                always exactly one colour — the two-tone `.is-combined` variant
                went with the combined board. */}
            <span
              className="circle-lead-dot shrink-0"
              style={
                {
                  '--dot': active?.color ?? 'var(--color-border-strong)',
                  borderColor: active?.color ?? 'var(--color-border-strong)',
                } as React.CSSProperties
              }
              aria-hidden
            />

            {/* The id lives here, on the text alone. It is what `board-header`
                reads with `innerText` and asserts equals the circle's name, so
                nothing else — no initial, no chevron — may sit inside it. */}
            <span id="board-title" className="board-title-text truncate">
              {label}
            </span>

            <ChevronDown
              className="size-4 shrink-0 text-ink-faint transition-transform duration-base ease-out-soft group-hover:text-ink-muted group-data-[state=open]:rotate-180"
              aria-hidden
            />
          </button>
        </Popover.Trigger>
      </Tip>

      {/* Mounted only while open. It was `forceMount` — so `board-header` could
          read a circle's colour off `.circle-opt .circle-dot` after the menu had
          closed — and that made a *closed* menu the topmost dismissable layer,
          which swallowed Escape from the task modal underneath. See the note in
          BoardControls; the same mistake, made for the same reason. */}
      <Popover.Portal>
        <Popover.Content
          id="circle-menu"
          align="start"
          sideOffset={8}
          className="motion-surface z-50 min-w-56 rounded-xl border border-border bg-surface p-1.5 shadow-overlay"
        >
          {/* The combined "All circles" entry used to sit here, above a divider.
              It is gone: a circle chooses its own columns now, and a board
              showing four circles' work has no set of columns it could honestly
              draw. Anyone who needs to look across circles has My Day, Archived
              and search, all of which already span them. */}
          {/* **A fixed ceiling, and the list scrolls inside it.**

              Nine circles made this menu taller than the cards behind it, and
              on a phone it ran off the screen — the same menu was a different
              shape for every person, which is the opposite of what a switcher
              should feel like. 16rem is about six rows: enough that most people
              never see a scrollbar, small enough that the menu stays a menu.

              The scroll is on the list alone, not on the whole popover, so
              "Show all circles" stays pinned at the bottom. Putting it inside
              would mean scrolling to the end of the very list you are trying to
              shorten in order to find the control that shortens it. */}
          <div className="circle-list max-h-[min(60vh,16rem)] overflow-y-auto">
          {shown.map((c) => (
            /* **A row, not a button.** It used to be one, and the Copy link
               control could not go inside it — a button nested in a button is
               invalid markup and browsers resolve it by dropping one of them.
               The row is a div now; the name is the button that switches, and
               the link is its own. */
            <div
              key={c.id}
              className={cn(
                'circle-opt group/row flex w-full items-center gap-2.5 rounded-lg pl-2.5 pr-1 transition-colors duration-fast ease-out-soft hover:bg-bg',
                c.id === data.activeId && 'font-semibold',
              )}
            >
              <button
                className="flex min-w-0 flex-1 items-center gap-2.5 py-2 text-left text-base"
                onClick={() => choose.mutate(c.id)}
              >
                <span className="circle-dot size-2.5 shrink-0 rounded-full" style={{ background: c.color }} />
                <span className="circle-opt-name truncate">{c.name}</span>
              </button>

              {/* The count gives way to the link on hover. Both sit in the same
                  slot so the row never changes width — a row that grows under
                  the pointer moves the one below it, which on a list you are
                  aiming at is worse than either control on its own. */}
              <span className="relative ml-auto grid size-7 shrink-0 place-items-center">
                <span className="text-2xs text-ink-faint group-hover/row:invisible">{c.memberCount}</span>
                <CopyCircleLink
                  circleId={c.id}
                  name={c.name}
                  className="invisible absolute inset-0 grid place-items-center rounded-md text-ink-faint transition-colors duration-fast ease-out-soft hover:text-ink group-hover/row:visible"
                />
              </span>
            </div>
          ))}
          </div>

          {/* Only where there is something folded away. For everybody but a
              super admin `others` is empty and this whole control is absent —
              a toggle that reveals nothing is a question with one answer. */}
          {others.length > 0 && (
            <button
              id="circle-show-all"
              onClick={toggleAll}
              aria-pressed={showAll}
              className="circle-show-all mt-1 flex w-full items-center justify-center gap-1 rounded-lg border-t border-border px-2.5 pb-1 pt-2 text-2xs text-ink-faint transition-colors duration-fast ease-out-soft hover:bg-bg hover:text-ink"
            >
              {showAll
                ? 'Show only my circles'
                : `Show all circles (${others.length} more)`}
            </button>
          )}
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
}
